import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { restartableTask, task } from 'ember-concurrency';
import FeaturesService from 'ember-feature-flags/services/features';
import EnvironmentService from 'cio/services/environment';
import { computed } from '@ember/object';
import { IdentifierKey, isAttributeEnabledIdentifier } from 'cio/utils/identifiers';
import CustomerModel from 'cio/models/customer';
import { htmlSafe } from '@ember/template';
import DS from 'ember-data';
import ApiService from 'cio/services/api';
import RelationshipsService from 'cio/services/relationships';

const ENABLE_SEARCH_AFTER = 10;
const DEVICES_DISPLAY_LIMIT = 5;

export default class IndexController extends Controller {
  @service api!: ApiService;
  @service features!: FeaturesService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @service('relationships') relationshipsService!: RelationshipsService;

  declare model: CustomerModel;

  searchTerm = '';

  get searchEnabled() {
    return this.model.filteredAttrsWithIdentifiers.length >= ENABLE_SEARCH_AFTER;
  }

  @computed('model.filteredAttrsWithIdentifiers.[]', 'searchTerm')
  get matchedAttrs() {
    let trimmedSearchTerm = this.searchTerm.trim();
    let identifiers: Record<string, string>[] = [];
    let attributes: Record<string, string>[] = [];

    this.model.filteredAttrsWithIdentifiers.forEach((attr: Record<string, string>) => {
      let [key] = Object.keys(attr);

      if (trimmedSearchTerm && !key.includes(trimmedSearchTerm)) {
        return;
      }

      if (isAttributeEnabledIdentifier(key, this.environment.current.identifiers)) {
        identifiers.push(attr);
      } else {
        attributes.push(attr);
      }
    });

    return {
      identifiers,
      attributes,
      attrsLength: identifiers.length + attributes.length,
    };
  }

  get filteredDevices() {
    return this.model.sortedDevices.slice(0, DEVICES_DISPLAY_LIMIT);
  }

  get moreDevicesLinkText() {
    let text = '';
    if (this.deviceCountIsSingle) {
      text = 'View device';
    } else if (this.deviceCountIsWithinLimit) {
      text = 'View devices';
    } else if (this.deviceCountExceedsLimit) {
      return htmlSafe(`View ${this.model.sortedDevices.length} devices`);
    }

    return text;
  }

  get deviceCountIsSingle() {
    return this.model.sortedDevices?.length === 1;
  }

  get deviceIsRegistered() {
    return this.model.sortedDevices?.length > 0;
  }

  get deviceCountIsWithinLimit() {
    return this.model.sortedDevices?.length <= DEVICES_DISPLAY_LIMIT && this.model.sortedDevices?.length > 1;
  }

  get deviceCountExceedsLimit() {
    return this.model.sortedDevices?.length > DEVICES_DISPLAY_LIMIT;
  }

  fetchLogs = restartableTask(async () => {
    return await this.model.queryLogs({ limit: 5 });
  });

  fetchDeliveries = task(async () => {
    return await this.store.query('delivery', {
      internal_id: this.model.identifiers[IdentifierKey.CioId],
      size: 5,
      states: 'sent,failed,bounced',
    });
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'person.index': IndexController;
  }
}
